import { useState, type FormEvent } from "react";
import { useDataProvider, useNotify, useTranslate } from "ra-core";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

import type { CrmDataProvider } from "../providers/types";
import type { QuoterConfig } from "../types";
import { useRefreshQuoterAdmin, useSaveQuoterConfig } from "./quoterAdminData";
import {
  CONFIG_NUMBER_FIELDS,
  configToFormValues,
  parseNumber,
  toConfigData,
  validateConfigForm,
  type ConfigFormValues,
} from "./quoterConfigRules";
import { QuoterField } from "./QuoterField";
import { TermImpactDialog } from "./TermImpactDialog";

/**
 * Almikia Epic EQ2 (TASK-099): the project quoter configuration (apartado,
 * validity, installments term, apartados-count flag, excess $/m2, released
 * stages, protection days). Changing the installments term does NOT save
 * anything until the "ver impacto" dialog is confirmed: the dialog applies
 * the term (recalculating the valid quotes) and then the rest is saved.
 */
export const QuoterConfigForm = ({ config }: { config: QuoterConfig }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const save = useSaveQuoterConfig();
  const refresh = useRefreshQuoterAdmin();
  const [values, setValues] = useState<ConfigFormValues>(() =>
    configToFormValues(config),
  );
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pendingTerm, setPendingTerm] = useState<number | null>(null);

  const errors = validateConfigForm(values);
  const set = <K extends keyof ConfigFormValues>(
    key: K,
    value: ConfigFormValues[K],
  ) => setValues((current) => ({ ...current, [key]: value }));
  const err = (field: keyof typeof errors) =>
    submitted ? (errors[field] ?? null) : null;

  const saveRest = async () => {
    await save("quoter_configs", config.id, toConfigData(values));
  };

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitted(true);
    if (Object.keys(errors).length > 0) return;
    const newTerm = parseNumber(values.term_months) ?? config.term_months;
    if (newTerm !== config.term_months) {
      // Nothing is saved or recalculated before the impact is confirmed.
      setPendingTerm(newTerm);
      return;
    }
    setSaving(true);
    try {
      await saveRest();
      notify("crm.quoter.config.saved");
    } catch {
      notify("crm.quoter.config.save_error", { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const confirmTerm = async () => {
    if (pendingTerm == null) return;
    await dataProvider.quoterApplyTerm(config.project_id, pendingTerm, true);
    try {
      await saveRest();
    } finally {
      await refresh();
    }
    setPendingTerm(null);
    notify("crm.quoter.config.saved");
  };

  return (
    <form onSubmit={onSubmit} noValidate>
      <Card>
        <CardContent className="space-y-4 p-4">
          <h2 className="text-lg font-semibold">
            {translate("crm.quoter.config.section_config")}
          </h2>
          <p className="text-base text-muted-foreground sm:text-sm">
            {translate("crm.quoter.config.config_intro")}
          </p>
          <div className="grid gap-4 sm:grid-cols-2">
            {CONFIG_NUMBER_FIELDS.map((field) => (
              <QuoterField
                key={field}
                id={`quoter-config-${field}`}
                label={translate(`crm.quoter.config.fields.${field}`)}
                value={values[field]}
                numeric
                error={err(field)}
                onChange={(value) => set(field, value)}
              />
            ))}
            <QuoterField
              id="quoter-config-released_stages"
              label={translate("crm.quoter.config.fields.released_stages")}
              value={values.released_stages}
              error={err("released_stages")}
              onChange={(value) => set("released_stages", value)}
            />
          </div>
          <label className="flex min-h-11 items-center gap-2 text-base sm:min-h-0 sm:text-sm">
            <input
              type="checkbox"
              checked={values.apartados_count_toward_list}
              onChange={(event) =>
                set("apartados_count_toward_list", event.target.checked)
              }
            />
            {translate("crm.quoter.config.fields.apartados_count_toward_list")}
          </label>
          <Button type="submit" className="h-11 sm:h-9" disabled={saving}>
            {translate("crm.quoter.config.save")}
          </Button>
        </CardContent>
      </Card>

      {pendingTerm != null ? (
        <TermImpactDialog
          projectId={config.project_id}
          newTerm={pendingTerm}
          onConfirm={confirmTerm}
          onCancel={() => setPendingTerm(null)}
        />
      ) : null}
    </form>
  );
};
